import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelection } from '../contexts/SelectionContext';
import { useTheme } from '../contexts/ThemeContext';
import { model3dKind } from '../lib/model-store';
import Layout from '../components/Layout';
import Viewer3DScene from '../components/Viewer3DScene';
import TerrainControls3D from '../components/terrain/TerrainControls3D';
import { hasXyz } from '../lib/dataset-points3d';
import { isScatterDataset } from '../lib/raw-scatter3d';
import { loadHill3dSample } from '../lib/sample-models';
import { useOptionalToast } from '../components/Toast';
import useViewer3dDatasets from '../hooks/useViewer3dDatasets';
import Viewer3DRibbon from '../components/Viewer3DRibbon';
import { X } from 'lucide-react';
import { useProjectTerrain } from '../hooks/useTerrainSettings';
import useViewer3dModels from '../hooks/useViewer3dModels';
import useViewer3dView from '../hooks/useViewer3dView';
import useViewer3dPersistence from '../hooks/useViewer3dPersistence';
import useTurntableExport from '../hooks/useTurntableExport';
import { lastProjectId } from '../lib/map-session';

const DEFAULT_LAYERS = { datasets: true, projects: true };
// Scene elements: [view state key, label, helper text]
const SCENE_ITEMS = [
  ['showGrid', 'Reference grid', 'Ground grid at the scene origin (off by default)'],
  ['showAxes', 'XYZ axes', 'Red / green / blue axes helper'],
  ['showSections', 'Sections (fences)', 'Fence diagrams of 2D models'],
  ['showTraces', 'Section ground traces', 'Yellow lines where the sections meet the ground'],
  ['showVolumes', 'Volumes', 'Blocks or points of 3D models, and their slices'],
  ['showBoreholes', 'Boreholes', 'Borehole logs of the project'],
  ['showOverlay', 'Overlay text', 'Points / colour status text, top left'],
];
const HOVER_STYLE = { position: 'fixed', zIndex: 60, pointerEvents: 'none', padding: '3px 8px', borderRadius: 6, fontSize: 11, whiteSpace: 'nowrap' };

function Viewer3D() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selection } = useSelection();
  const { C } = useTheme();
  // The project DEM link lives in the terrain store, which only the map page filled: opened
  // directly (or reloaded) /3d had no DEM and drew the flat ground image instead.
  useProjectTerrain(searchParams.get('project'));
  const [focusLine, setFocusLine] = useState(null);

  // Load the survey line handed off from the 2D map ("View in 3D"),
  // falling back to the cross-view selection context
  useEffect(() => {
    if (searchParams.get('focus')) {
      try {
        const stored = sessionStorage.getItem('viewer3d-focus');
        if (stored) {
          setFocusLine(JSON.parse(stored));
          return;
        }
      } catch { /* ignore malformed payload */ }
    }
    if (selection?.type === 'line' && selection.coordinates?.length >= 2) {
      setFocusLine({
        name: selection.name,
        coordinates: selection.coordinates,
        clickPoint: selection.position,
      });
    }
  }, [searchParams, selection]);

  // Stored inversion models of ?project= (V.12): /3d opened by URL shows them too.
  // Without ?project= (sidebar, bookmark, reload) the viewer had no models and no DEM:
  // fall back to the project the map worked on last and put it in the URL.
  const projectId = searchParams.get('project');
  const datasetParam = searchParams.get('dataset'); // only that survey's models, camera fitted to them
  const modelParam = searchParams.get('model');     // one stored model: selected, drives the camera fit
  useEffect(() => {
    if (projectId || datasetParam || modelParam) return;
    const last = lastProjectId();
    if (!last) return;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('project', last); return next; }, { replace: true });
  }, [projectId, datasetParam, modelParam, setSearchParams]);
  const { datasets, datasetsLoading: loading, storedLoading, loaded, total, fenceModels, shownModels, focusedIds, fitModelIds, addLocalModel } = useViewer3dModels({ projectId, datasetId: datasetParam, modelId: modelParam });

  // ── Left "Layers" panel state ──
  const [visibleLayers, setVisibleLayers] = useState(DEFAULT_LAYERS);
  const restoreLayers = useCallback((l) => setVisibleLayers((prev) => ({ ...prev, ...l })), []);
  const layerState = useMemo(() => ({ visible: visibleLayers, restore: restoreLayers, defaults: DEFAULT_LAYERS }), [visibleLayers, restoreLayers]);
  const [showLayerPanel, setShowLayerPanel] = useState(true);
  // Datasets with x/y/z drawn as point clouds: opt-in per dataset (each holds up to 200k points)
  const { drawnIds, toggleDrawn, points, scatterIds, toggleScatter, scatter, restoreTicks } = useViewer3dDatasets(datasets);
  const datasetTicks = useMemo(() => ({ drawnIds, scatterIds, restore: restoreTicks }), [drawnIds, scatterIds, restoreTicks]);
  // Hover text of the resistivity scatter point under the cursor: { text, x, y } | null
  const [scatterHover, setScatterHover] = useState(null);

  // Ribbon > Insert > Load sample: the bundled EarthImager 3D demo volume as a browser-only model
  const toast = useOptionalToast();
  const showToast = toast?.showToast;
  const [sampleBusy, setSampleBusy] = useState(false);
  const loadSample = async () => {
    setSampleBusy(true);
    try {
      const entry = await loadHill3dSample();
      addLocalModel(entry);
      if (showToast) showToast(`Loaded ${entry.name}`, 'success', 3500);
    } catch (err) {
      if (showToast) showToast(`Could not load the sample: ${err?.response?.data?.detail || err.message}`, 'error', 5000);
    } finally {
      setSampleBusy(false);
    }
  };

  const toggleLayer = (layer) => {
    setVisibleLayers(prev => ({
      ...prev,
      [layer]: !prev[layer]
    }));
  };

  const view = useViewer3dView();
  const { colorMode, onCameraPreset, showVolumePanel, setShowVolumePanel, showFences, setShowFences, terrainReady, resetVolumeFilters } = view;

  // ── Export (Reports / Share tabs) — driven through a ref into the canvas ──
  const viewerRef = useRef(null);
  // Saved view per project (autosave + restore) and the turntable video
  const { resetSavedView } = useViewer3dPersistence({ projectId, view, viewerRef, ready: !storedLoading && !loading, datasetTicks, layers: layerState, onResetCamera: () => onCameraPreset('reset') });
  const turntable = useTurntableExport({ viewerRef, name: projectId });

  const ribbonElement = (
    <Viewer3DRibbon
      // View tab
      onCameraPreset={onCameraPreset}
      onResetSavedView={projectId ? resetSavedView : undefined}
      turntable={turntable}
      colorMode={colorMode}
      setColorMode={view.setColorMode}
      showGrid={view.showGrid}
      onToggleGrid={() => view.setShowGrid((v) => !v)}
      showAxes={view.showAxes}
      onToggleAxes={() => view.setShowAxes((v) => !v)}
      // Volume tab
      showVolumePanel={showVolumePanel}
      onToggleVolumePanel={() => setShowVolumePanel(v => !v)}
      onResetVolumeFilters={resetVolumeFilters}
      showFences={showFences}
      onToggleFences={() => setShowFences(v => !v)}
      // Insert tab
      onImportInversionModel={() => navigate(projectId ? `/map?project=${encodeURIComponent(projectId)}` : '/map')}
      onLoadSample={loadSample}
      sampleBusy={sampleBusy}
      onAddData={() => navigate('/upload')}
      // Reports tab
      onExportImage={() => viewerRef.current?.exportImage()}
      // Share tab
      onExportModel={() => viewerRef.current?.exportModel()}
    />
  );

  return (
    <Layout ribbon={ribbonElement}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Focused line banner — a slim strip, never a document-style block */}
        {focusLine && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 14px', background: C.accentBg, borderBottom: `1px solid ${C.accentBorder}`, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: C.accent, fontWeight: 500 }}>
              📍 Viewing from 2D map: {focusLine.name || 'Survey line'}
            </span>
            <button
              onClick={() => { setFocusLine(null); sessionStorage.removeItem('viewer3d-focus'); }}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 2 }}
              title="Clear focus">
              <X size={13} />
            </button>
          </div>
        )}

        {/* ── Main layout ── */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Layer panel */}
          {showLayerPanel && (
            <div style={{ width: 220, background: C.bg2, borderRight: `1px solid ${C.border}`, overflow: 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600, color: C.text0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Layers
                <button onClick={() => setShowLayerPanel(false)}
                  style={{ fontSize: 12, background: 'none', border: 'none', color: C.text3, cursor: 'pointer', padding: '2px 4px' }} title="Collapse">‹</button>
              </div>

              <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
                {/* Base layers */}
                <div style={{ fontSize: 9, color: C.text3, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '4px 4px 6px' }}>Base Layers</div>
                {[
                  { id: 'datasets', label: 'Point Cloud', desc: 'Subsurface dataset points' },
                  { id: 'projects', label: 'Projects', desc: 'Project boundaries' },
                ].map(l => (
                  <div key={l.id} style={{ marginBottom: 6, padding: '8px 10px', background: C.bg3, borderRadius: 8, border: `1px solid ${visibleLayers[l.id] ? C.border : 'transparent'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: visibleLayers[l.id] ? C.text0 : C.text3, flex: 1 }}>{l.label}</span>
                      <label style={{ cursor: 'pointer' }}>
                        <div onClick={() => toggleLayer(l.id)}
                          style={{ width: 32, height: 18, borderRadius: 9, background: visibleLayers[l.id] ? C.accent : C.bg4, border: `1px solid ${visibleLayers[l.id] ? C.accentBorder : C.border}`, position: 'relative', transition: 'all .2s' }}>
                          <div style={{ position: 'absolute', top: 2, left: visibleLayers[l.id] ? 16 : 2, width: 12, height: 12, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                        </div>
                      </label>
                    </div>
                    <div style={{ fontSize: 9, color: C.text3, marginTop: 2 }}>{l.desc}</div>
                  </div>
                ))}

                {/* Scene elements: one switch per object of the scene */}
                <div style={{ fontSize: 9, color: C.text3, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '14px 4px 6px' }}>Scene</div>
                <div data-testid="viewer3d-scene-toggles">
                {SCENE_ITEMS.map(([key, label, desc]) => (
                  <div key={key} style={{ marginBottom: 6, padding: '8px 10px', background: C.bg3, borderRadius: 8, border: `1px solid ${view[key] ? C.border : 'transparent'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: view[key] ? C.text0 : C.text3, flex: 1 }}>{label}</span>
                      <button type="button" role="switch" aria-checked={!!view[key]} aria-label={label} data-testid={`scene-toggle-${key}`}
                        onClick={() => view[`set${key.slice(0, 1).toUpperCase()}${key.slice(1)}`]((v) => !v)}
                        style={{ width: 32, height: 18, borderRadius: 9, background: view[key] ? C.accent : C.bg4, border: `1px solid ${view[key] ? C.accentBorder : C.border}`, position: 'relative', transition: 'all .2s', padding: 0, cursor: 'pointer' }}>
                        <span style={{ position: 'absolute', top: 2, left: view[key] ? 16 : 2, width: 12, height: 12, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                      </button>
                    </div>
                    <div style={{ fontSize: 9, color: C.text3, marginTop: 2 }}>{desc}</div>
                  </div>
                ))}
                </div>

                <div style={{ fontSize: 9, color: C.text3, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '14px 4px 6px' }}>DEM terrain</div>
                <TerrainControls3D C={C} ready={terrainReady} />

                {/* Datasets */}
                <div style={{ fontSize: 9, color: C.text3, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '14px 4px 6px' }}>
                  Datasets ({datasets.length})
                </div>
                {loading && <div style={{ fontSize: 10, color: C.text2, padding: '0 4px' }}>Loading…</div>}
                {!loading && datasets.length === 0 && (
                  <div style={{ fontSize: 10, color: C.text2, padding: '0 4px' }}>No datasets loaded</div>
                )}
                <div style={{ maxHeight: 240, overflowY: 'auto' }} data-testid="viewer3d-dataset-list">
                {datasets.map(d => {
                  const drawable = hasXyz(d);
                  const scatterable = isScatterDataset(d);
                  return (
                    <React.Fragment key={d.id}>
                    <label title={drawable ? 'Draw as a point cloud' : 'No x/y/z column mapping: not drawn'}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: C.bg3, borderRadius: 6, marginBottom: 4, fontSize: 10, color: drawable ? C.text1 : C.text3, overflow: 'hidden', cursor: drawable ? 'pointer' : 'default' }}>
                      <input type="checkbox" disabled={!drawable} checked={drawable && drawnIds.includes(d.id)} onChange={() => toggleDrawn(d.id)} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || d.filename || d.id}</span>
                      {points.errors[d.id] && (
                        <span role="alert" title={points.errors[d.id]} style={{ color: '#f87171', flexShrink: 0 }}>✗ could not load points: {points.errors[d.id]}</span>
                      )}
                    </label>
                    {scatterable && (
                      <label title="One point per reading at its pseudo-position, coloured by apparent resistivity"
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px 3px 24px', marginTop: -2, marginBottom: 4, fontSize: 10, color: C.text1, cursor: 'pointer' }}>
                        <input type="checkbox" checked={scatterIds.includes(d.id)} onChange={() => toggleScatter(d.id)} />
                        <span>Apparent resistivity scatter</span>
                        {scatter.errors[d.id] && (
                          <span role="alert" title={scatter.errors[d.id]} style={{ color: '#f87171', flexShrink: 0 }}>✗ {scatter.errors[d.id]}</span>
                        )}
                      </label>
                    )}
                    </React.Fragment>
                  );
                })}
                </div>

                {/* Inversion models: fence diagrams and volumes */}
                <div style={{ fontSize: 9, color: C.text3, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '14px 4px 6px' }}>
                  Inversion Models ({fenceModels.length})
                </div>
                {storedLoading && <div style={{ fontSize: 10, color: C.text2, padding: '0 4px' }}>{total > 0 ? `Loading models ${Math.min(loaded + 1, total)} of ${total}…` : 'Loading stored models…'}</div>}
                {!storedLoading && fenceModels.length === 0 && (
                  <div style={{ fontSize: 10, color: C.text2, padding: '0 4px' }}>None imported yet</div>
                )}
                {fenceModels.length > 0 && (
                  <div data-testid="viewer3d-model-list" style={{ marginBottom: 6, padding: '8px 10px', background: C.bg3, borderRadius: 8, border: `1px solid ${showFences ? 'rgba(167,139,250,0.3)' : 'transparent'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: showFences ? C.text0 : C.text3, flex: 1 }}>Models ({shownModels.length} drawn)</span>
                      <label style={{ cursor: 'pointer' }}>
                        <div onClick={() => setShowFences(v => !v)}
                          style={{ width: 32, height: 18, borderRadius: 9, background: showFences ? '#a78bfa' : C.bg4, border: `1px solid ${showFences ? 'rgba(167,139,250,0.35)' : C.border}`, position: 'relative', transition: 'all .2s' }}>
                          <div style={{ position: 'absolute', top: 2, left: showFences ? 16 : 2, width: 12, height: 12, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                        </div>
                      </label>
                    </div>
                    {fenceModels.map(f => {
                      const kind = model3dKind(f);
                      return (
                        <div key={f.id} data-testid="viewer3d-model-row" data-focused={focusedIds.includes(f.id) ? 'true' : undefined}
                          style={{ fontSize: 9, color: focusedIds.includes(f.id) ? C.accent : kind ? C.text2 : C.text3, fontWeight: focusedIds.includes(f.id) ? 700 : 400, marginTop: 4 }}
                          title={kind ? undefined : 'Not drawn: define its coordinates on the map (1D soundings are not drawn in 3D)'}>
                          {kind === 'volume' ? '🧊' : '📐'} {f.name || f.model?.name || f.id}{kind ? '' : ' — not drawn'}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 10, color: C.text2, marginBottom: 2 }}>COLOR MODE</div>
                <div style={{ fontSize: 11, color: C.accent, textTransform: 'capitalize' }}>{colorMode}</div>
              </div>
            </div>
          )}
          {!showLayerPanel && (
            <button onClick={() => setShowLayerPanel(true)} title="Expand Layers"
              style={{ width: 20, background: C.bg2, border: 'none', borderRight: `1px solid ${C.border}`, color: C.text3, cursor: 'pointer', flexShrink: 0 }}>
              ›
            </button>
          )}

          {/* ── 3D Canvas — fills remaining space; the docked Volume panel
                lives inside the scene and is never an overlay ── */}
          <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
            <Viewer3DScene
              ref={viewerRef}
              view={view}
              datasets={datasets}
              drawnIds={drawnIds}
              points={points}
              scatterIds={scatterIds}
              scatter={scatter}
              onScatterHover={setScatterHover}
              focusLine={focusLine}
              fenceModels={fenceModels}
              fitModelIds={fitModelIds}
              showPointCloud={visibleLayers.datasets}
            />
          </div>
        </div>
      </div>
      {scatterHover && (
        <div data-testid="viewer3d-scatter-tooltip" style={{ ...HOVER_STYLE, left: scatterHover.x + 12, top: scatterHover.y + 12, background: C.bg2, border: `1px solid ${C.border}`, color: C.text0 }}>
          {scatterHover.text}
        </div>
      )}
    </Layout>
  );
}

export default Viewer3D;
